import React, { useState, useRef, useEffect } from "react";
import { Settings, Check } from "lucide-react";

export interface ColumnOption {
    field: string;
    header: string;
    required?: boolean;
}

interface ColumnSelectorProps {
    columns: ColumnOption[];
    visibleColumns: Set<string>;
    onChange: (visibleColumns: Set<string>) => void;
}

const ColumnSelector: React.FC<ColumnSelectorProps> = ({ columns, visibleColumns, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    const handleToggle = (field: string) => {
        const column = columns.find((c) => c.field === field);
        if (column?.required && visibleColumns.has(field)) return;

        const newVisible = new Set(visibleColumns);
        if (newVisible.has(field)) {
            newVisible.delete(field);
        } else {
            newVisible.add(field);
        }
        onChange(newVisible);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-200 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 hover:border-neutral-400 dark:hover:border-neutral-600 transition-all shadow-sm"
            >
                <Settings size={16} />
                <span>Columns</span>
                <span className="px-2 py-0.5 text-xs font-semibold text-primary-700 dark:text-primary-300 bg-primary-100 dark:bg-primary-900/50 rounded-full min-w-[24px] text-center">
                    {visibleColumns.size}
                </span>
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-3 w-64 bg-white dark:bg-neutral-800 rounded-xl shadow-xl border border-neutral-200 dark:border-neutral-700 z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-neutral-100 dark:border-neutral-700 bg-neutral-50/50 dark:bg-neutral-800">
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Toggle Columns</span>
                            <span className="text-xs text-neutral-400 dark:text-neutral-500">
                                {visibleColumns.size} of {columns.length}
                            </span>
                        </div>
                    </div>
                    <div className="max-h-72 overflow-y-auto py-1.5">
                        {columns.map((column) => {
                            const isVisible = visibleColumns.has(column.field);
                            const isRequired = column.required;

                            return (
                                <label
                                    key={column.field}
                                    className={`flex items-center gap-3 px-4 py-2.5 transition-colors ${
                                        isRequired
                                            ? "opacity-40 cursor-not-allowed"
                                            : "hover:bg-neutral-50 dark:hover:bg-neutral-700/50 cursor-pointer"
                                    }`}
                                >
                                    <div className="relative flex items-center flex-shrink-0">
                                        <input
                                            type="checkbox"
                                            checked={isVisible}
                                            onChange={() => handleToggle(column.field)}
                                            disabled={isRequired}
                                            className="sr-only"
                                        />
                                        <div
                                            className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                                                isVisible
                                                    ? isRequired
                                                        ? "bg-neutral-300 dark:bg-neutral-600 border-neutral-300 dark:border-neutral-600"
                                                        : "bg-primary-600 dark:bg-primary-500 border-primary-600 dark:border-primary-500 shadow-sm"
                                                    : "bg-white dark:bg-neutral-800 border-neutral-300 dark:border-neutral-600"
                                            }`}
                                        >
                                            {isVisible && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                        </div>
                                    </div>
                                    <span className="text-sm text-neutral-700 dark:text-neutral-300">{column.header}</span>
                                </label>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ColumnSelector;
